import {
  SkyBadge,
  SkyButton,
  SkyMessageBar,
  SkyPopover,
  SkySwitch,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState, type ReactElement } from "react";
import { Link, useLocation } from "wouter";
import type {
  DbtJobHistory,
  DbtJobHistoryAttribution,
  DbtJobHistoryModel,
  DbtJobHistoryModelOutcome,
  DbtJobHistoryRun,
} from "@shared/types/dbtJobHistory";
import { EmptyState, ErrorState } from "@/components/access/ScreenStates";
import { usePermissions } from "@/hooks/use-permissions";
import { useDbtJobHistory, type DbtJobHistoryPage } from "@/hooks/use-dbt-jobs";
import { absolutePath } from "@/lib/routes";
import { CauseBlock } from "./CauseBlock";
import { INLINED_BY_DBT_SENTENCE } from "@/lib/transformations/modelGroups";
import {
  asSentence,
  formatRows,
  formatSeconds,
  historyOutcomePresentation,
  kindLabel,
  materializationLabel,
  runStartText,
  stepStatusPresentation,
  type Presentation,
} from "./transformFormatting";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * The History tab of a transformation (session 7, canvas 18; FR-62, SEC-17).
 *
 * A matrix: one row per model the transformation's selector selects, one
 * column per transform step that included it, newest on the RIGHT. It is a
 * native `<table>` with scoped headers, so the matrix is its own accessible
 * alternative: every cell is a button whose name reads the model, the run,
 * the outcome, the time, the tests and the cause, and whose popover holds the
 * same facts with the classified cause (O3's `CauseBlock`).
 *
 * Nothing here composes a cause: the sentence is the server's, and a failed
 * outcome without one reads "Cause not classified". dbt's own message and the
 * SQL never reach this screen.
 *
 * "Load older runs" asks for the next cursor and PREPENDS older columns; rows
 * are merged by unique id and a model absent from a page reads "Not built"
 * there. "Show upstream models" adds the manifest ancestors of the member
 * models (`include=dependencies`) under their own heading.
 */

export interface ModelHistoryTimelineProps {
  dbtJobId: string;
  dbtJobName: string;
}

interface MergedHistory {
  head: DbtJobHistory;
  /** Oldest first: the display order, newest on the right. */
  runs: DbtJobHistoryRun[];
  models: DbtJobHistoryModel[];
  storageError: string | null;
}

const TERMINAL = new Set(["succeeded", "failed", "cancelled", "skipped"]);

function padOutcome(run: DbtJobHistoryRun): DbtJobHistoryModelOutcome {
  return {
    runId: run.runId,
    outcome: TERMINAL.has(run.stepStatus) ? "not_reported" : "running",
    status: null,
    executionTimeSeconds: null,
    rowsAffected: null,
    materialization: null,
    tests: null,
    failedTests: [],
    cause: null,
  };
}

/** Pages arrive newest first; the merged view is oldest first, with rows merged by id. */
function mergePages(pages: readonly DbtJobHistoryPage[]): MergedHistory | null {
  const head = pages[0]?.data;
  if (!head) return null;
  const runs = pages.flatMap((page) => page.data.runs).reverse();
  const rows = new Map<string, DbtJobHistoryModel>();
  const outcomes = new Map<string, DbtJobHistoryModelOutcome>();
  for (const page of pages) {
    for (const model of page.data.models) {
      if (!rows.has(model.uniqueId)) rows.set(model.uniqueId, model);
      for (const outcome of model.outcomes) {
        outcomes.set(`${model.uniqueId}|${outcome.runId}`, outcome);
      }
    }
  }
  const models = [...rows.values()].map((model) => ({
    ...model,
    outcomes: runs.map((run) => outcomes.get(`${model.uniqueId}|${run.runId}`) ?? padOutcome(run)),
  }));
  const storageError = pages.find((page) => page.data.artifacts.storageError)?.data.artifacts
    .storageError;
  return { head, runs, models, storageError: storageError ?? null };
}

/**
 * A failed column's label: by the phase it stopped in, when the run says. #1207: the word the
 * header, the Runs tab and the Jobs view give the same run (`stepStatusPresentation`), so a run
 * stopped at the clone reads "Could not clone" here too.
 */
function failedLabel(exitClass: DbtJobHistoryRun["exitClass"]): string {
  return stepStatusPresentation("failed", exitClass).label;
}

/** The step's outcome in the header's words: the exit class names a failure. */
function stepWords(run: DbtJobHistoryRun): {
  label: string;
  color: Presentation["color"];
} {
  switch (run.stepStatus) {
    case "pending":
      return { label: "Queued", color: "informative" };
    case "running":
      return { label: "Running", color: "informative" };
    case "succeeded":
      return { label: "Succeeded", color: "success" };
    case "skipped":
      return { label: "Skipped", color: "warning" };
    case "cancelled":
      return { label: "Cancelled", color: "subtle" };
    default:
      return {
        label: failedLabel(run.exitClass),
        color: "danger",
      };
  }
}

/** True when the column's run stopped before dbt built anything. */
function stoppedBeforeDbt(run: DbtJobHistoryRun): boolean {
  return (
    TERMINAL.has(run.stepStatus) &&
    !run.modelDetail &&
    run.exitClass !== null &&
    run.exitClass !== "ok"
  );
}

function testsLine(outcome: DbtJobHistoryModelOutcome): string | null {
  const tests = outcome.tests;
  if (!tests || tests.total === 0) return null;
  return `${tests.passed}/${tests.total} tests`;
}

/** #1437: an ephemeral model dbt reported nothing for is inlined, not unbuilt. */
function isInlinedModel(model: DbtJobHistoryModel, outcome: DbtJobHistoryModelOutcome): boolean {
  return outcome.outcome === "not_reported" && model.materialized === "ephemeral";
}

/** The two short lines inside a cell. */
function cellLines(
  outcome: DbtJobHistoryModelOutcome,
  model: DbtJobHistoryModel,
): [string, string | null] {
  const presentation = historyOutcomePresentation(outcome.outcome);
  const tests = testsLine(outcome);
  switch (outcome.outcome) {
    case "succeeded":
      return [formatSeconds(outcome.executionTimeSeconds), tests];
    case "failed":
      return ["Failed", outcome.cause?.label ?? tests];
    case "running":
      return ["Running", "results at the end"];
    case "unknown":
      return ["Unknown", null];
    default:
      // #1437: dbt inlines an ephemeral model and reports nothing for it, so it was not "Not built".
      return [isInlinedModel(model, outcome) ? "Inlined" : presentation.label, tests];
  }
}

function cellName(
  model: DbtJobHistoryModel,
  run: DbtJobHistoryRun,
  outcome: DbtJobHistoryModelOutcome,
  zone: string,
): string {
  const parts = [
    model.name,
    `run ${run.runId}, ${runStartText(run, zone, "short")}`,
    isInlinedModel(model, outcome)
      ? "Inlined by dbt"
      : historyOutcomePresentation(outcome.outcome).label,
  ];
  // #1207: dbt's own word only when the product has none for it ("Status not recognised").
  if (outcome.outcome === "unknown" && outcome.status && outcome.status !== "unknown")
    parts.push(`dbt status ${outcome.status}`);
  if (outcome.executionTimeSeconds !== null)
    parts.push(formatSeconds(outcome.executionTimeSeconds));
  if (outcome.tests && outcome.tests.total > 0) {
    parts.push(`${outcome.tests.passed} of ${outcome.tests.total} tests passed`);
  }
  if (outcome.cause) parts.push(outcome.cause.label);
  return parts.join(", ");
}

function testDetailWords(run: DbtJobHistoryRun): string {
  switch (run.testDetail) {
    case "pending":
      return "Test results are read when the run finishes.";
    case "unreadable":
      return "This run's test results could not be read.";
    case "not_run":
      return "No test results for this run.";
    default:
      return "No tests cover this model.";
  }
}

/** A failed model's cause, or that it was not classified. */
function failedCause(outcome: DbtJobHistoryModelOutcome): ReactElement {
  if (outcome.cause) return <CauseBlock whyOnlyWhenGeneric cause={outcome.cause} compact />;
  return (
    <SkyText size={200} color="muted" testId="history-cause-unclassified">
      Cause not classified
    </SkyText>
  );
}

/** The run's test counts for the model, else (once it is not running) why there are none. */
function testsSummary(
  run: DbtJobHistoryRun,
  outcome: DbtJobHistoryModelOutcome,
): ReactElement | null {
  const tests = outcome.tests;
  if (tests) {
    return (
      <SkyText size={200}>
        Tests: {tests.passed} passed · {tests.failed} failed
        {tests.warned ? ` · ${tests.warned} warned` : ""}
        {tests.skipped ? ` · ${tests.skipped} skipped` : ""}
      </SkyText>
    );
  }
  if (outcome.outcome !== "running") {
    return (
      <SkyText size={200} color="muted">
        {testDetailWords(run)}
      </SkyText>
    );
  }
  return null;
}

/** What the outcome says beyond its badge: time, rows and build, why nothing was reported, the cause. */
function OutcomeNotes({
  model,
  run,
  outcome,
}: {
  model: DbtJobHistoryModel;
  run: DbtJobHistoryRun;
  outcome: DbtJobHistoryModelOutcome;
}) {
  return (
    <>
      {outcome.outcome === "succeeded" || outcome.outcome === "failed" ? (
        <SkyText size={200}>
          {formatSeconds(outcome.executionTimeSeconds)}
          {outcome.rowsAffected !== null ? ` · ${formatRows(outcome.rowsAffected)}` : ""}
          {outcome.materialization ? ` · ${outcome.materialization}` : ""}
        </SkyText>
      ) : null}
      {isInlinedModel(model, outcome) ? (
        <SkyText size={200} color="muted">
          {INLINED_BY_DBT_SENTENCE}
        </SkyText>
      ) : null}
      {outcome.outcome === "not_reported" && !isInlinedModel(model, outcome) ? (
        <SkyText size={200} color="muted">
          dbt reported nothing for this model in this run
          {stoppedBeforeDbt(run) ? `: the run stopped in the ${run.exitClass} phase.` : "."}
        </SkyText>
      ) : null}
      {outcome.outcome === "unknown" ? (
        <SkyText size={200} color="muted">
          dbt reported a status this screen does not recognise, so it is not read as a success or a
          failure.
        </SkyText>
      ) : null}
      {outcome.outcome === "failed" ? failedCause(outcome) : null}
    </>
  );
}

/** The run's tests of the model, or why there are none, and the failed tests with their causes. */
function OutcomeTests({
  run,
  outcome,
}: {
  run: DbtJobHistoryRun;
  outcome: DbtJobHistoryModelOutcome;
}) {
  return (
    <>
      {testsSummary(run, outcome)}
      {outcome.failedTests.length > 0 ? (
        <ul className="flex flex-col gap-2" aria-label="Failed tests">
          {outcome.failedTests.map((test) => (
            <li key={test.uniqueId} className="flex flex-col gap-1">
              <SkyText size={200} font="monospace" className="break-all">
                {test.name}
                {test.failures !== null ? ` · ${test.failures.toLocaleString()} found` : ""}
              </SkyText>
              {test.cause ? <CauseBlock whyOnlyWhenGeneric cause={test.cause} compact /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function OutcomeDetails({
  model,
  run,
  outcome,
}: {
  model: DbtJobHistoryModel;
  run: DbtJobHistoryRun;
  outcome: DbtJobHistoryModelOutcome;
}) {
  const zone = useDisplayTz();
  const presentation = historyOutcomePresentation(outcome.outcome);
  return (
    <div
      className="flex w-80 max-w-[85vw] flex-col gap-2 p-3"
      data-testid="history-outcome-details"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SkyText size={300} weight="semibold">
          {model.name}
        </SkyText>
        <SkyBadge appearance="tint" color={presentation.color}>
          {presentation.label}
        </SkyBadge>
        {/* #1207: one word for each result. dbt's own word only when the product has none. */}
        {outcome.outcome === "unknown" && outcome.status && outcome.status !== "unknown" ? (
          <SkyText size={200} color="muted" font="monospace">
            {outcome.status}
          </SkyText>
        ) : null}
      </div>
      <SkyText size={200} color="muted">
        Run <span className="font-mono">{run.runId}</span> · {kindLabel(run.kind).toLowerCase()} ·{" "}
        {runStartText(run, zone, "seconds")}
      </SkyText>
      <OutcomeNotes model={model} run={run} outcome={outcome} />
      <OutcomeTests run={run} outcome={outcome} />
      <Link
        href={absolutePath(`/runs/${run.runId}`)}
        className="text-sm underline underline-offset-2"
      >
        Open run {run.runId}
      </Link>
    </div>
  );
}

function RowHeader({ model, runs }: { model: DbtJobHistoryModel; runs: DbtJobHistoryRun[] }) {
  const zone = useDisplayTz();
  const counted = model.outcomes.filter((outcome) => outcome.outcome !== "running");
  const succeeded = counted.filter((outcome) => outcome.outcome === "succeeded").length;
  let lastFailure: DbtJobHistoryRun | undefined;
  model.outcomes.forEach((outcome, index) => {
    if (outcome.outcome === "failed") lastFailure = runs[index];
  });
  const latestRows = [...model.outcomes].reverse().find((outcome) => outcome.rowsAffected !== null);
  return (
    <th
      scope="row"
      className="sticky left-0 z-10 min-w-[12rem] border-b border-border bg-card px-3 py-2 text-left align-top font-normal"
    >
      <SkyText size={200} weight="semibold" font="monospace" block>
        {model.name}
      </SkyText>
      <SkyText size={200} color="muted" block>
        {[
          model.materialized ? materializationLabel(model.materialized) : model.resourceType,
          latestRows ? formatRows(latestRows.rowsAffected) : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </SkyText>
      <SkyText size={200} color="muted" block testId={`history-summary-${model.name}`}>
        {succeeded} of {counted.length} succeeded
        {lastFailure ? ` · last failure ${runStartText(lastFailure, zone, "short")}` : ""}
      </SkyText>
    </th>
  );
}

/** Why the history shows every model, and a storage read that failed. */
function HistoryNotices({
  attribution,
  storageError,
  dbtJobName,
}: {
  attribution: DbtJobHistoryAttribution;
  storageError: string | null;
  dbtJobName: string;
}) {
  return (
    <>
      {!attribution.known ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title="Showing every model these runs built"
          message={`Which models ${dbtJobName} selects is not known (${attribution.unsupported ?? "the selector could not be evaluated"}), so the rows are the models the runs reported rather than a guess.`}
          testId="history-attribution-unknown"
        />
      ) : null}
      {storageError ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          title="Some run artifacts could not be read"
          message={`Storage answered ${storageError}, so some of this history could not be read: test results are missing where a run's cell says so. The Destination's storage identity needs read access to the staging container.`}
          testId="history-storage-warning"
        />
      ) : null}
    </>
  );
}

export function ModelHistoryTimeline({ dbtJobId, dbtJobName }: ModelHistoryTimelineProps) {
  const zone = useDisplayTz();
  const [, navigate] = useLocation();
  const { canAny } = usePermissions();
  const canRetry = canAny("runs.retry");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [upstream, setUpstream] = useState(false);
  const [openCell, setOpenCell] = useState<string | null>(null);

  const history = useDbtJobHistory(dbtJobId, {
    enabled: true,
    include: upstream ? "dependencies" : undefined,
  });
  const merged = useMemo(
    () => (history.data ? mergePages(history.data.pages) : null),
    [history.data],
  );

  if (history.isPending) {
    return (
      <div
        className="grid h-40 place-items-center rounded-lg border border-border bg-card"
        aria-busy="true"
        data-testid="model-history-loading"
      >
        <SkyText size={200} color="muted" role="status">
          Loading the model history…
        </SkyText>
      </div>
    );
  }
  if (history.isError && !merged) {
    return (
      <ErrorState
        title="We could not load the model history"
        error={history.error}
        onRetry={() => void history.refetch()}
      />
    );
  }
  if (!merged || merged.runs.length === 0) {
    return (
      <EmptyState
        title="No runs yet"
        message="This dbt job has not been part of a transform run yet. Its models and each run's outcome appear here after the first one."
      />
    );
  }

  const { head, runs } = merged;
  const newest = runs[runs.length - 1];
  const failedModels = merged.models.filter((model) =>
    model.outcomes.some((outcome) => outcome.outcome === "failed"),
  );
  const visible = onlyFailed ? failedModels : merged.models;
  const noRowsText = onlyFailed
    ? "No model failed in these runs."
    : "The selector selects no model in the latest project.";
  const members = visible.filter((model) => model.member);
  const upstreamRows = visible.filter((model) => !model.member);

  const renderRow = (model: DbtJobHistoryModel) => (
    <tr key={model.uniqueId} data-testid={`history-row-${model.name}`}>
      <RowHeader model={model} runs={runs} />
      {model.outcomes.map((outcome, index) => {
        const run = runs[index] as DbtJobHistoryRun;
        const key = `${model.uniqueId}|${run.runId}`;
        const presentation = historyOutcomePresentation(outcome.outcome);
        const [line1, line2] = cellLines(outcome, model);
        return (
          <td key={run.runId} className="border-b border-border px-1 py-1.5 align-top">
            <SkyPopover
              open={openCell === key}
              onOpenChange={(open) => setOpenCell(open ? key : null)}
              placement="bottom"
              className="p-0"
              testId={`history-popover-${model.name}-${run.runId}`}
              trigger={
                <button
                  type="button"
                  aria-label={cellName(model, run, outcome, zone)}
                  aria-haspopup="dialog"
                  aria-expanded={openCell === key}
                  data-testid={`history-cell-${model.name}-${run.runId}`}
                  data-outcome={outcome.outcome}
                  onClick={() => setOpenCell(openCell === key ? null : key)}
                  className={`flex min-h-10 w-full min-w-[5rem] flex-col items-center justify-center rounded-sm px-1 py-0.5 text-xs leading-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${presentation.cellClass}`}
                >
                  <span className="font-semibold tabular-nums">
                    <span aria-hidden="true">{presentation.glyph}</span> {line1}
                  </span>
                  {line2 ? <span className="max-w-[7rem] truncate">{line2}</span> : null}
                </button>
              }
            >
              <OutcomeDetails model={model} run={run} outcome={outcome} />
            </SkyPopover>
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="flex flex-col gap-3" data-testid="model-history">
      <div className="flex flex-wrap items-center gap-4" data-testid="model-history-toolbar">
        <SkySwitch
          label="Only models that failed"
          checked={onlyFailed}
          onCheckedChange={setOnlyFailed}
          testId="history-only-failed"
        />
        <SkySwitch
          label="Show upstream models"
          checked={upstream}
          onCheckedChange={(checked) => {
            setOpenCell(null);
            setUpstream(checked);
          }}
          testId="history-show-upstream"
        />
        <SkyText size={200} color="muted" className="ml-auto">
          {runs.length} run{runs.length === 1 ? "" : "s"}, newest on the right
        </SkyText>
      </div>

      {canRetry && newest && newest.stepStatus === "failed" ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="The latest run failed"
          message={`${asSentence(newest.error ?? "The dbt run failed")} The run's page shows the failed step and, where the run allows it, retries it from there.`}
          testId="history-latest-failed"
          actions={
            <SkyButton
              appearance="secondary"
              size="small"
              onClick={() => navigate(absolutePath(`/runs/${newest.runId}`))}
            >
              Open run {newest.runId}
            </SkyButton>
          }
        />
      ) : null}

      <HistoryNotices
        attribution={head.attribution}
        storageError={merged.storageError}
        dbtJobName={dbtJobName}
      />

      {visible.length === 0 ? (
        <SkyText size={200} color="muted" testId="history-no-rows">
          {noRowsText}
        </SkyText>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full border-collapse" data-testid="model-history-table">
            <caption className="sr-only">
              Model history for {dbtJobName}: one row per model, one column per run, newest on the
              right
            </caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 border-b border-border bg-card px-3 py-2 text-left"
                >
                  <SkyText size={200} color="muted" className="uppercase tracking-wider">
                    Model
                  </SkyText>
                </th>
                {runs.map((run) => {
                  const words = stepWords(run);
                  return (
                    <th
                      key={run.runId}
                      scope="col"
                      className="border-b border-border px-1 py-2 text-center align-top font-normal"
                      data-testid="history-run-head"
                      data-run-id={run.runId}
                    >
                      <Link
                        href={absolutePath(`/runs/${run.runId}`)}
                        className="block text-xs tabular-nums underline underline-offset-2"
                        aria-label={`Run ${run.runId}, ${runStartText(run, zone, "short")}`}
                      >
                        {runStartText(run, zone, "short")}
                      </Link>
                      <SkyBadge appearance="tint" color={words.color}>
                        {words.label}
                      </SkyBadge>
                      {stoppedBeforeDbt(run) ? (
                        <SkyText size={200} color="muted" block testId="history-run-stopped">
                          {/* #1207: in words, never the exit class or phase token. */}
                          No model results recorded.
                        </SkyText>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>{members.map(renderRow)}</tbody>
            {upstreamRows.length > 0 ? (
              <tbody data-testid="history-upstream-group">
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={runs.length + 1}
                    className="border-b border-border bg-muted px-3 py-1.5 text-left"
                  >
                    <SkyText size={200} color="muted" className="uppercase tracking-wider">
                      Upstream models
                    </SkyText>
                  </th>
                </tr>
                {upstreamRows.map(renderRow)}
              </tbody>
            ) : null}
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {history.hasNextPage ? (
          <SkyButton
            appearance="secondary"
            loading={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
            testId="history-load-older"
          >
            Load older runs
          </SkyButton>
        ) : null}
        {history.isFetchNextPageError ? (
          <SkyText size={200} color="muted" role="alert" testId="history-load-older-error">
            The older runs could not be loaded. Try again.
          </SkyText>
        ) : null}
        <ul
          className="ml-auto flex flex-wrap gap-3"
          aria-label="Legend"
          data-testid="history-legend"
        >
          {(["succeeded", "failed", "skipped", "not_reported", "running", "unknown"] as const).map(
            (kind) => {
              const presentation = historyOutcomePresentation(kind);
              return (
                <li key={kind} className="flex items-center gap-1">
                  <span
                    aria-hidden="true"
                    className={`inline-flex h-4 w-5 items-center justify-center rounded-sm text-xs ${presentation.cellClass}`}
                  >
                    {presentation.glyph}
                  </span>
                  <SkyText size={200} color="muted">
                    {presentation.label}
                  </SkyText>
                </li>
              );
            },
          )}
        </ul>
      </div>
    </div>
  );
}
